import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { adClient, adMode, adsAllowedOn, adSlotId, FORMATS, loadAdSense, PLACEMENTS } from "@/lib/ads";
import { useI18n } from "@/contexts/I18nContext";

/**
 * One ad unit. Renders nothing when ads are off, on a blocked route, or for an
 * unknown placement. Always labelled, always reserves its height, and never
 * styled like site content (no card, no cover shape, no button look).
 *
 * SPA safety: the unit is keyed by the route (location.key), so navigating
 * mounts a fresh <ins>; each <ins> is pushed exactly once (ref guard plus the
 * data-adsbygoogle-status attribute AdSense sets when it has processed it).
 */
export function AdSlot({ placement, className = "" }) {
  const location = useLocation();
  const { t } = useI18n();
  const mode = adMode(placement);
  if (mode === "off" || !adsAllowedOn(location.pathname)) return null;
  const { format } = PLACEMENTS[placement];
  const height = FORMATS[format];
  return (
    <aside
      aria-label={t("advertisement")}
      data-testid={`ad-${placement}`} data-ad-mode={mode}
      className={`ad-slot mx-auto w-full ${format === "rectangle" ? "max-w-[300px]" : "max-w-[970px]"} ${className}`}
      style={{ "--ad-h-mobile": `${height.mobile}px`, "--ad-h-desktop": `${height.desktop}px` }}
    >
      <p className="mb-1 text-[0.72rem] text-muted-foreground">{t("advertisement")}</p>
      {mode === "live"
        ? <LiveUnit key={location.key} placement={placement} format={format} />
        : <div className="ad-box flex items-center justify-center border border-dashed border-muted-foreground/40 text-xs text-muted-foreground">
            {placement} · {format}
          </div>}
    </aside>
  );
}

function LiveUnit({ placement, format }) {
  const ref = useRef(null);
  const pushed = useRef(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const ins = ref.current;
    if (!ins) return undefined;
    loadAdSense();
    if (!pushed.current && !ins.getAttribute("data-adsbygoogle-status")) {
      pushed.current = true;
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) { /* AdSense reports its own errors */ }
    }
    // No fill: collapse only when the unit is off-screen, so the reader never sees a jump.
    const obs = new MutationObserver(() => {
      if (ins.getAttribute("data-ad-status") === "unfilled" && ins.getBoundingClientRect().top > window.innerHeight) {
        setCollapsed(true);
      }
    });
    obs.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    return () => obs.disconnect();
  }, []);

  if (collapsed) return null;
  return (
    <ins
      ref={ref}
      className="adsbygoogle ad-box block"
      data-ad-client={adClient()}
      data-ad-slot={adSlotId(placement)}
      data-ad-format={format === "rectangle" ? "rectangle" : "horizontal"}
      data-full-width-responsive={format === "rectangle" ? "false" : "true"}
    />
  );
}
